import { produce } from 'immer';
import { UserNote } from '@/types/UserNote';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { Example } from '@/types/Example';
import { BaseCommand } from '@/types/BaseCommand';
import { Flag } from '@/types/Flag';
import { UserEvent } from '@/types/UserEvent';
import { QuizAnswer } from '@/types/QuizAnswer';

export type UserNoteStore = Record<string, UserNote>;

interface StoreState {
  initialized?: boolean;
  userNotes: UserNoteStore;
  examples: Record<string, Example>;
  baseCommands: Record<string, BaseCommand>;
  userEvents: UserEvent[];
  quizAnswers: QuizAnswer[];
  flags: Record<string, Flag>;
  isCheatsheetEditMode: boolean;
  showSignInModal: boolean;
}

const useZStore = create<StoreState>(() => ({
  initialized: false,
  userNotes: {},
  examples: {},
  baseCommands: {},
  userEvents: [],
  quizAnswers: [],
  flags: {},
  isCheatsheetEditMode: false,
  showSignInModal: false,
}));

export const setInitialized = (initialized: boolean) =>
  useZStore.setState((state) => ({ ...state, initialized }));

export const setExamples = (examples: Example[]) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      examples.forEach((example) => {
        draftState.examples[example.id] = example;
      });
    });
    return newState;
  });

export const setBaseCommands = (baseCommands: BaseCommand[]) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      baseCommands.forEach((baseCommand) => {
        draftState.baseCommands[baseCommand.id] = baseCommand;
      });
    });
    return newState;
  });

export const setFlags = (flags: Flag[]) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      flags.forEach((flag) => {
        draftState.flags[flag.id] = flag;
      });
    });
    return newState;
  });

export const setExample = (example: Example) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      draftState.examples[example.id] = example;
    });
    return newState;
  });

export const setUserNotes = (userNotes: UserNote[]) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      userNotes.forEach((userNote) => {
        draftState.userNotes[userNote.example_id] = userNote;
      });
    });
    return newState;
  });

export const setQuizAnswers = (quizAnswers: QuizAnswer[]) =>
  useZStore.setState((state) => {
    return { ...state, quizAnswers };
  });

export const setUserEvents = (userEvents: UserEvent[]) =>
  useZStore.setState((state) => {
    return { ...state, userEvents };
  });

export const setUserNote = (userNote: UserNote) =>
  useZStore.setState((state) => {
    const newState = {
      ...state,
      userNotes: { ...state.userNotes, [userNote.example_id]: userNote },
    };
    return newState;
  });

export const setCheatsheetEditMode = (isCheatsheetEditMode: boolean) =>
  useZStore.setState((state) => {
    return { ...state, isCheatsheetEditMode };
  });

export const removeUserNote = (exampleId: string) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      delete draftState.userNotes[exampleId];
    });
    return newState;
  });

export const hideNote = (exampleId: string) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      draftState.userNotes[exampleId].for_study = false;
    });
    return newState;
  });

export const unHideNote = (exampleId: string) =>
  useZStore.setState((state) => {
    const newState = produce(state, (draftState) => {
      draftState.userNotes[exampleId].for_study = true;
    });
    return newState;
  });

const defaultUserNote: Omit<UserNote, 'example_id'> = {
  bookmarked: false,
  for_study: false,
};

export const getUserNoteById =
  (exampleId: string, provideDefault = false) =>
  (state: StoreState) => {
    const userNote = state.userNotes[exampleId];
    return (
      userNote ||
      (provideDefault ? { ...defaultUserNote, example_id: exampleId } : null)
    );
  };

export const getBookmarkedUserNotesIds = () => (state: StoreState) => {
  const userNotesArray = Object.values(state.userNotes);
  const bookmarkedNotes = userNotesArray
    .filter((m) => m.bookmarked)
    .map((m) => m.example_id);
  return bookmarkedNotes;
};

export const getReviewUserNotesIds = () => (state: StoreState) => {
  const userNotesArray = Object.values(state.userNotes);
  return userNotesArray.filter((m) => m.for_study).map((m) => m.example_id);
};

export const getUserNonVisitEvents = () => (state: StoreState) => {
  const events = Object.values(state.userEvents);
  const nonVisitEvents = events.filter((event) => event.event_type !== 'visit');
  return nonVisitEvents;
};

// TO-DO: Rethink this, it's AI generated and may be sub-optimal
export const getReviewExamples = () => (state: StoreState) => {
  const userNotesArray = Object.values(state.userNotes);
  const examples = Object.values(state.examples);
  return examples.filter((example) => {
    const userNote = userNotesArray.find((m) => m.example_id === example.id);
    return userNote?.for_study || false;
  });
};

export const getExamples = () => (state: StoreState) => {
  return Object.values(state.examples);
};

export const getCommands = () => (state: StoreState) => {
  return Object.values(state.baseCommands);
};

export const getFlags = () => (state: StoreState) => {
  return Object.values(state.flags).map((flag) => flag.flag);
};

export const getNextExampleToLearn = (count: number) => (state: StoreState) => {
  const examples = Object.values(state.examples);
  const userNotes = Object.values(state.userNotes);
  const examplesIds = examples.map((e) => e.id);
  const userNotesIds = userNotes.map((e) => e.example_id);
  const examplesIdsToLearn = examplesIds.filter(
    (e) => !userNotesIds.includes(e),
  );

  const exampleIndex = count % examplesIdsToLearn.length;
  return examples.find((e) => e.id === examplesIdsToLearn[exampleIndex]);
};
export const getExamplesToLearn = () => (state: StoreState) => {
  const examples = Object.values(state.examples);
  const userNotes = Object.values(state.userNotes);
  const userNotesIds = userNotes.map((e) => e.example_id);
  const examplesToLearn = examples.filter(
    (example) => !userNotesIds.includes(example.id),
  );
  return examplesToLearn;
};

export const getBookmarkedExampleIds = () => (state: StoreState) => {
  const userNotes = Object.values(state.userNotes);
  const userNotesIds = userNotes
    .filter((m) => m.bookmarked)
    .map((m) => m.example_id);
  return userNotesIds;
};

export default useZStore;
